// Story 1.20 — TanStack Query hook for GET /api/v1/account/dsar-summary.
//
// Returns aggregate counts + active-request-ids per DSAR surface.
// Drives the GdprPage's status-driven rendering — knowing "is there an
// active export?" + "what's the latest erasure scheduled_for?" in one
// round-trip avoids fanning out to 4 separate endpoints on each page
// render.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type DsarSummaryExportSection = {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  active_request_id: string | null;
};

export type DsarSummaryErasureSection = {
  active_request_id: string | null;
  scheduled_for: string | null;
};

export type DsarSummaryRectificationSection = {
  submitted: number;
  completed: number;
  rejected: number;
};

export type DsarSummary = {
  export: DsarSummaryExportSection;
  portability: DsarSummaryExportSection;
  erasure: DsarSummaryErasureSection;
  rectification: DsarSummaryRectificationSection;
};

export type DsarSummaryEnvelope = {
  data: DsarSummary;
};

async function fetchDsarSummary(): Promise<DsarSummaryEnvelope> {
  const response = await fetch("/api/v1/account/dsar-summary", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`dsar-summary fetch failed: ${response.status}`);
  }
  return (await response.json()) as DsarSummaryEnvelope;
}

export function useDsarStatus() {
  return useQuery({
    queryKey: ["dsar-status"],
    queryFn: fetchDsarSummary,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
